/* eslint-disable react/prop-types */
import { CHAIN_COLUMNS } from 'components/DataExpansionComponents/Chains/chainColumnsInfo'
import { CommonFiltersetAccountGroup } from 'components/DataExpansionComponents/Common/CommonTable/CommonFiltersetAccountGroup'
import { createFilterColumnHelper } from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import { filterStoreRepo } from 'components/Filters/FilterStore'
import FilterModal, {
  FilterGroup,
  QuickFilterGroup,
} from 'components/Filters/components/FilterModal'
import {
  QuickRangeFilter,
  QuickValueFilter,
} from 'components/Filters/fields/QuickFilter'
import {
  RangeFilter,
  RangeFilterWithMinMax,
} from 'components/Filters/fields/RangeFilter'
import { SelectFilter } from 'components/Filters/fields/SelectFilter'
import { ZipcodeFilter } from 'components/Filters/fields/ZipcodeFilter'
import { DMA_OPTIONS } from 'models/dma_options'
import { InstagramTextPercentileOptions } from 'models/instagram_text_percentile'
import { menu_ingredients } from 'models/menu_ingredients'
import { REPUTATION_PLATFORMS_OPTIONS } from 'models/reputation_platforms'
import { FilterIdentifier } from 'models/saved_view'
import { VelocityGroupOptions } from 'models/velocity_groups'
import useApiService from 'services/api'
import { ALL_COUNTRIES } from 'utils/countries'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { Cuisine50Options } from '../../../models/cuisine_50'
import { SERVICE_OPTIONS } from 'models/services'
import { ATMOSPHERES_OPTIONS } from 'models/atmospheres'
import { OFFERINGS_OPTIONS } from 'models/offerings'
import { OPEN_HOURS_OPTIONS } from 'models/openHours'
import { CityAndStateFilter } from 'components/Filters/components/CityAndStateFilter'

export function ChainsFilterset({
  filterIdentifier,
  hideCampaignsFilter,
  hiddenColumnIds,
}: {
  filterIdentifier: FilterIdentifier
  hideCampaignsFilter: boolean
  hiddenColumnIds?: string[]
}) {
  const chainsFilterStore = filterStoreRepo.getStore(filterIdentifier)

  const api = useApiService()
  const featureFlags = useFeatureFlagService()
  const { data: campaignOptions } = api.useGetCampaignsOptions(
    'restaurants-bars',
    undefined,
    true
  )
  const visibleColumns = Object.fromEntries(
    Object.entries(CHAIN_COLUMNS).filter(
      ([key]) => !hiddenColumnIds?.includes(key)
    )
  )
  const chainFilterHelper = createFilterColumnHelper({
    columns: visibleColumns,
  })
  const getFilterProps = (key: string) => {
    const props = chainFilterHelper.retrieveProps(key)
    return {
      ...props,
      filterKey: String(props.filterKey),
    }
  }

  return (
    <FilterModal identifier={filterIdentifier} store={chainsFilterStore}>
      <QuickFilterGroup>
        <QuickRangeFilter
          {...getFilterProps('one_year_total_value')}
          valueLabel="≥1"
          predefinedValue={{ min: 1 }}
          filterStore={chainsFilterStore}
        />
        <QuickRangeFilter
          {...getFilterProps('count')}
          valueLabel="2+"
          predefinedValue={{ min: 2 }}
          filterStore={chainsFilterStore}
        />
        <QuickValueFilter
          {...getFilterProps('sales_stages')}
          valueLabel="Empty"
          predefinedValue={[{ value: '-1', label: 'Empty' }]}
          filterStore={chainsFilterStore}
        />
        <QuickRangeFilter
          {...getFilterProps('contact_count')}
          valueLabel="≤0"
          predefinedValue={{ max: 0 }}
          filterStore={chainsFilterStore}
        />
        {!hideCampaignsFilter && !hiddenColumnIds?.includes('campaigns') && (
          <QuickValueFilter
            {...getFilterProps('campaigns')}
            valueLabel="No Campaign"
            predefinedValue={[{ label: 'No Campaign', value: 'REMOVE' }]}
            filterStore={chainsFilterStore}
          />
        )}
      </QuickFilterGroup>

      <FilterGroup title="Lifetime Values">
        <RangeFilter
          {...getFilterProps('total_ltv')}
          store={chainsFilterStore}
        />
        <RangeFilter
          {...getFilterProps('brand_ltv')}
          store={chainsFilterStore}
        />
        <RangeFilter
          {...getFilterProps('revenue_ltv')}
          store={chainsFilterStore}
        />
      </FilterGroup>

      <FilterGroup title="Predictive Annual Values">
        <RangeFilter
          {...getFilterProps('one_year_total_value')}
          store={chainsFilterStore}
        />
        <RangeFilter {...getFilterProps('taro')} store={chainsFilterStore} />
        <RangeFilter {...getFilterProps('tabo')} store={chainsFilterStore} />
      </FilterGroup>

      <FilterGroup title="Menu">
        <SelectFilter
          {...getFilterProps('cuisine_50')}
          options={Cuisine50Options}
          filterStore={chainsFilterStore}
        />
        <RangeFilter
          {...getFilterProps('pounds_per_year')}
          store={chainsFilterStore}
        />
        {!featureFlags.hideIngredientsMatrix && (
          <SelectFilter
            {...getFilterProps('ingredients_matrix')}
            options={menu_ingredients}
            filterStore={chainsFilterStore}
          />
        )}
        {featureFlags.shouldShowMenuSize && (
          <RangeFilter
            {...getFilterProps('menu_length')}
            store={chainsFilterStore}
          />
        )}
        <RangeFilter
          {...getFilterProps('menu_matches')}
          store={chainsFilterStore}
        />
      </FilterGroup>

      <FilterGroup title="Features">
        <RangeFilter {...getFilterProps('count')} store={chainsFilterStore} />
        <SelectFilter
          {...getFilterProps('expense_category')}
          options={[
            { label: '$', value: '1' },
            { label: '$$', value: '2' },
            { label: '$$$', value: '3' },
            { label: '$$$$', value: '4' },
          ]}
          filterStore={chainsFilterStore}
        />
        {featureFlags.shouldShowReputationData && (
          <>
            <SelectFilter
              {...getFilterProps('reputation_data')}
              options={REPUTATION_PLATFORMS_OPTIONS}
              filterStore={chainsFilterStore}
            />
            <RangeFilterWithMinMax
              {...getFilterProps('michelin_stars_count')}
              store={chainsFilterStore}
              min={0}
              max={3}
            />
          </>
        )}
        <RangeFilterWithMinMax
          {...getFilterProps('rating')}
          store={chainsFilterStore}
          min={0}
          max={5}
          decimalScale={2}
        />
        <RangeFilter
          {...getFilterProps('reviews_count')}
          store={chainsFilterStore}
        />
        <SelectFilter
          {...getFilterProps('velocity_group')}
          options={VelocityGroupOptions}
          filterStore={chainsFilterStore}
        />
        {featureFlags.enableInstagremData && (
          <RangeFilter
            {...getFilterProps('instagram_followers')}
            store={chainsFilterStore}
          />
        )}
        {featureFlags.enableInstagramPercentile && (
          <SelectFilter
            {...getFilterProps('instagram_text_percentile')}
            options={InstagramTextPercentileOptions}
            filterStore={chainsFilterStore}
          />
        )}
        {featureFlags.enableOppsAttrsServicesAtmosOffersHours && (
          <>
            <SelectFilter
              {...getFilterProps('services')}
              options={SERVICE_OPTIONS}
              filterStore={chainsFilterStore}
            />
            <SelectFilter
              {...getFilterProps('atmospheres')}
              options={ATMOSPHERES_OPTIONS}
              filterStore={chainsFilterStore}
            />
            <SelectFilter
              {...getFilterProps('offerings')}
              options={OFFERINGS_OPTIONS}
              filterStore={chainsFilterStore}
            />
            <SelectFilter
              {...getFilterProps('open_hours')}
              options={OPEN_HOURS_OPTIONS}
              filterStore={chainsFilterStore}
            />
          </>
        )}
      </FilterGroup>

      <CommonFiltersetAccountGroup
        hideCampaignsFilter={hideCampaignsFilter}
        filterStore={chainsFilterStore}
        campaignOptions={campaignOptions}
      />

      <FilterGroup title="Location">
        {featureFlags.enableRestaurantsCountryFilter && (
          <SelectFilter
            {...getFilterProps('country_summary')}
            options={ALL_COUNTRIES}
            filterStore={chainsFilterStore}
          />
        )}
        <CityAndStateFilter
          filterStore={chainsFilterStore}
          stateSelectProps={getFilterProps('state_summary')}
          citySelectProps={getFilterProps('city_summary')}
          cityKey="city_summary"
          stateKey="state_summary"
          companyType="restaurants-bars"
        />
        <SelectFilter
          {...getFilterProps('dma_summary')}
          options={DMA_OPTIONS}
          filterStore={chainsFilterStore}
        />
        <ZipcodeFilter filterStore={chainsFilterStore} />
        {featureFlags.enableHQStateColumn && (
          <>
            <CityAndStateFilter
              filterStore={chainsFilterStore}
              stateSelectProps={getFilterProps('hq_state1')}
              citySelectProps={getFilterProps('hq_city1')}
              stateKey="hq_state1"
              cityKey="hq_city1"
            />
            <CityAndStateFilter
              filterStore={chainsFilterStore}
              stateSelectProps={getFilterProps('hq_state2')}
              citySelectProps={getFilterProps('hq_city2')}
              stateKey="hq_state2"
              cityKey="hq_city2"
            />
          </>
        )}
      </FilterGroup>

      <FilterGroup title="ZIP Code Demographics">
        <RangeFilter
          {...getFilterProps('median_hhi')}
          store={chainsFilterStore}
        />
        <RangeFilter
          {...getFilterProps('hh_gt100k')}
          store={chainsFilterStore}
        />
        <RangeFilter
          {...getFilterProps('pop_density')}
          store={chainsFilterStore}
        />
      </FilterGroup>
    </FilterModal>
  )
}
